import { ReloadOutlined } from '@ant-design/icons';
import { Alert, Button, Empty, Spin, Typography } from 'antd';

import { MAX_SELECTED_INSTAGRAM_PUBLICATIONS } from '@app/components/Modals/BotDetailsModal/constants/instagramPublications';

import {
  useInstagramPublicationsGrid,
  type UseInstagramPublicationsGridProps,
} from '../hooks/useInstagramPublicationsGrid';
import { useInstagramPublicationsGridEffects } from '../hooks/useInstagramPublicationsGridEffects';

import { InstagramPublicationCard } from './InstagramPublicationCard';

import type { FC } from 'react';

import './InstagramPublicationsGrid.scss';

const { Text } = Typography;

// Поле формы: Form.Item передаёт value (instagramId выбранных) и onChange.
type InstagramPublicationsGridProps = UseInstagramPublicationsGridProps;

export const InstagramPublicationsGrid: FC<InstagramPublicationsGridProps> = (
  props
) => {
  const {
    isEnabled,
    isLoadedForIntegration,
    loadFirstPage,
    publications,
    selectedIdsSet,
    selectedCount,
    isLimitReached,
    unloadedBelowCount,
    missingSelectedCount,
    postsRefreshNote,
    hasMore,
    isFirstPageLoading,
    isRefreshing,
    isLoadingMore,
    firstPageError,
    loadMoreError,
    isEmpty,
    isFirstPageFailed,
    isRefreshFailed,
    refreshHandler,
    loadNextPageHandler,
    retryNextPageHandler,
    scrollHandler,
    togglePublicationHandler,
    clearSelectionHandler,
    removeUnloadedSelectionHandler,
  } = useInstagramPublicationsGrid(props);

  useInstagramPublicationsGridEffects({
    isEnabled,
    isLoadedForIntegration,
    loadFirstPage,
  });

  return (
    <div className="InstagramPublicationsGrid">
      <div className="InstagramPublicationsGrid__toolbar">
        <Text data-testid="instagram-publications-selected-count">
          Выбрано публикаций: {selectedCount} (можно до{' '}
          {MAX_SELECTED_INSTAGRAM_PUBLICATIONS})
        </Text>

        {selectedCount > 0 && (
          <Button type="link" size="small" onClick={clearSelectionHandler}>
            Убрать всё из выбора
          </Button>
        )}

        <Button
          size="small"
          icon={<ReloadOutlined aria-hidden />}
          loading={isRefreshing}
          disabled={isFirstPageLoading}
          onClick={refreshHandler}
          className="InstagramPublicationsGrid__refresh"
        >
          Обновить список
        </Button>
      </div>

      <Text type="secondary" className="InstagramPublicationsGrid__note">
        За одно обновление CRM загружает до 200 последних публикаций.
      </Text>

      {postsRefreshNote && (
        <Text
          type="secondary"
          className="InstagramPublicationsGrid__note"
          data-testid="instagram-publications-refresh-note"
        >
          {postsRefreshNote}
        </Text>
      )}

      {isRefreshFailed && (
        <Text
          type="danger"
          className="InstagramPublicationsGrid__note"
          data-testid="instagram-publications-refresh-error"
        >
          Не удалось обновить список:{' '}
          {firstPageError || 'попробуйте ещё раз через минуту.'}
        </Text>
      )}

      {unloadedBelowCount > 0 && (
        <Text
          type="secondary"
          className="InstagramPublicationsGrid__note"
          data-testid="instagram-publications-selected-below"
        >
          Выбранных публикаций ниже в списке: {unloadedBelowCount}. Выбор
          сохранится.
        </Text>
      )}

      {missingSelectedCount > 0 && (
        <div className="InstagramPublicationsGrid__missing">
          <Text
            type="secondary"
            className="InstagramPublicationsGrid__note"
            data-testid="instagram-publications-selected-missing"
          >
            Выбранных публикаций нет в списке: {missingSelectedCount}. Робот
            продолжит отвечать на комментарии под ними.
          </Text>
          <Button
            type="link"
            size="small"
            onClick={removeUnloadedSelectionHandler}
          >
            Убрать их из выбора
          </Button>
        </div>
      )}

      {isLimitReached && (
        <Text type="warning" className="InstagramPublicationsGrid__note">
          Выбрано {MAX_SELECTED_INSTAGRAM_PUBLICATIONS} публикаций — это предел.
          Чтобы выбрать другую публикацию, уберите одну из выбранных.
        </Text>
      )}

      {isFirstPageLoading && (
        <div className="InstagramPublicationsGrid__state">
          <Spin />
        </div>
      )}

      {isFirstPageFailed && (
        <Alert
          type="error"
          showIcon
          data-testid="instagram-publications-error"
          message="Не удалось загрузить публикации Instagram"
          description={firstPageError || 'Попробуйте ещё раз через минуту.'}
          action={
            <Button size="small" onClick={refreshHandler}>
              Повторить
            </Button>
          }
        />
      )}

      {isEmpty && (
        <Empty
          className="InstagramPublicationsGrid__state"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <span data-testid="instagram-publications-empty">
              В списке пока нет публикаций. Если аккаунт подключили недавно, CRM
              ещё загружает их из Instagram. Подождите несколько минут и нажмите
              «Обновить список».
            </span>
          }
        />
      )}

      {publications.length > 0 && (
        <div
          className="InstagramPublicationsGrid__scroll"
          onScroll={scrollHandler}
          data-testid="instagram-publications-scroll"
        >
          <div
            className="InstagramPublicationsGrid__list"
            role="group"
            aria-label="Публикации Instagram"
          >
            {publications.map((publication) => {
              const isSelected = selectedIdsSet.has(publication.instagramId);

              return (
                <InstagramPublicationCard
                  key={publication.instagramId}
                  publication={publication}
                  isSelected={isSelected}
                  isDisabled={isLimitReached && !isSelected}
                  onToggle={togglePublicationHandler}
                />
              );
            })}
          </div>

          <div className="InstagramPublicationsGrid__footer">
            {isLoadingMore && <Spin size="small" />}

            {loadMoreError && (
              <Text type="danger">
                Не удалось загрузить следующие публикации: {loadMoreError}{' '}
                <Button type="link" size="small" onClick={retryNextPageHandler}>
                  Повторить
                </Button>
              </Text>
            )}

            {hasMore && !isLoadingMore && !loadMoreError && (
              <Button type="link" size="small" onClick={loadNextPageHandler}>
                Показать ещё
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
